import React, { useState, useEffect, useMemo } from "react";
import { FruitifyContainer } from "../../../shared-components/Containers";
import { Box, Typography, Button } from "@mui/material";
import { Add } from "@mui/icons-material";
import SchoolSearchAutocomplete from "../../school/components/SchoolSearchAutocomplete";
import RagDocumentUploadHistory from "../../personalisation/components/RagDocumentUploadHistory";
import RagDocumentUploadDialog from "../../personalisation/components/RagDocumentUploadDialog";
import { usePagination } from "../../../hooks/usePagination";
import { useApiClient } from "../../../api";

export default function SuperuserRagManagement() {
  const ApiClient = useApiClient();
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  const filters = useMemo(() => {
    return {
      school: selectedSchool?.id,
    };
  }, [selectedSchool]);

  const {
    options: uploads,
    loading,
    page,
    pageInfo,
    handlePageChange,
  } = usePagination(
    ({ first, after }) =>
      selectedSchool
        ? ApiClient.rag.getDocumentUploads({
            first,
            after,
            ...filters,
          })
        : Promise.resolve({ edges: [], pageInfo: { hasNextPage: false } }),
    10,
    filters,
  );

  const refetchCurrentPage = () => {
    handlePageChange(null, page);
  };

  const handleSchoolChange = (event, newValue) => {
    setSelectedSchool(newValue);
  };

  const handleUploadSuccess = () => {
    setUploadDialogOpen(false);
    refetchCurrentPage();
  };

  useEffect(() => {
    if (page !== 1 || !selectedSchool) return;

    const interval = setInterval(() => {
      handlePageChange(null, page);
    }, 10000);

    return () => clearInterval(interval);
  }, [page, selectedSchool, handlePageChange]);

  return (
    <FruitifyContainer>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          RAG Document Management
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="body1" color="text.secondary">
            Upload and manage RAG documents for schools
          </Typography>
          {selectedSchool && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setUploadDialogOpen(true)}
            >
              Upload Document
            </Button>
          )}
        </Box>

        <SchoolSearchAutocomplete
          school={selectedSchool}
          onChange={handleSchoolChange}
        />
      </Box>

      {selectedSchool && (
        <>
          <RagDocumentUploadHistory
            uploads={uploads}
            loading={loading}
            page={page}
            pageInfo={pageInfo}
            onPageChange={handlePageChange}
            onRefetch={refetchCurrentPage}
          />

          <RagDocumentUploadDialog
            open={uploadDialogOpen}
            onClose={() => setUploadDialogOpen(false)}
            onUploadSuccess={handleUploadSuccess}
            schoolId={selectedSchool.id}
          />
        </>
      )}

      {!selectedSchool && (
        <Box
          sx={{
            mt: 4,
            p: 4,
            textAlign: "center",
            backgroundColor: "background.paper",
            borderRadius: 2,
          }}
        >
          <Typography variant="body1" color="text.secondary">
            Select a school to view and manage RAG documents
          </Typography>
        </Box>
      )}
    </FruitifyContainer>
  );
}
